import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  Input,
  inject,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { Lesson, Module } from 'core/models/course-content.model';
import { LessonHeader } from 'components/lesson-header/lesson-header';

@Component({
  selector: 'app-video-content',
  standalone: true,
  imports: [CommonModule, LessonHeader],
  templateUrl: './video.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'w-full h-full flex flex-col',
  },
})
export class Video {
  private readonly sanitizer = inject(DomSanitizer);

  @Input({ required: true }) lesson!: Lesson;
  @Input() module?: Module | null;

  protected get safeVideoUrl(): SafeResourceUrl | null {
    if (!this.lesson?.videoUrl) {
      return null;
    }
    return this.sanitizer.bypassSecurityTrustResourceUrl(this.lesson.videoUrl);
  }

  /**
   * Converte URL de embed do YouTube para URL normal do YouTube
   * Exemplo: https://www.youtube.com/embed/VIDEO_ID -> https://www.youtube.com/watch?v=VIDEO_ID
   */
  protected get youtubeWatchUrl(): string | null {
    if (!this.lesson?.videoUrl) {
      return null;
    }

    // Se já for um URL de watch, retorna como está
    if (this.lesson.videoUrl.includes('youtube.com/watch')) {
      return this.lesson.videoUrl;
    }

    // Se for um embed URL, converte para watch URL
    const embedMatch = this.lesson.videoUrl.match(
      /youtube\.com\/embed\/([a-zA-Z0-9_-]+)/,
    );
    if (embedMatch && embedMatch[1]) {
      return `https://www.youtube.com/watch?v=${embedMatch[1]}`;
    }

    // Se for um youtu.be URL, converte para watch URL
    const shortMatch = this.lesson.videoUrl.match(
      /youtu\.be\/([a-zA-Z0-9_-]+)/,
    );
    if (shortMatch && shortMatch[1]) {
      return `https://www.youtube.com/watch?v=${shortMatch[1]}`;
    }

    // Retorna o URL original se não conseguir converter
    return this.lesson.videoUrl;
  }
}
